import * as React from 'react';
import { useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { Button, ErrorMessage } from '@components/common';
import { Label } from '@components/fields';
import { DraggableFields } from '@components/shared/DraggableFields';
import { compact } from '@components/utils/collections';
import { useFocusInput } from '@hooks/useFocusInput';

export const Matrix: QuestionCardBodyComponent = ({ question: q, onChange }) => {
  const [rows, setRows] = React.useState<string[]>(q.matrix_options?.rows || []);
  const [columns, setColumns] = React.useState<string[]>(q.matrix_options?.columns || []);

  const { focusInput } = useFocusInput();

  const [rowsError, setRowsError] = useState(false);
  const [columnsError, setColumnsError] = useState(false);
  const [columnsLimitError, setColumnsLimitError] = useState(false);

  const { callback: debouncedChange } = useDebouncedCallback(onChange, 300);

  const dataNotValid = compact(rows).length === 0 || compact(columns).length === 0;
  const someRowsAreEmpty = rows.some((row) => row === '');
  const someColumnsAreEmpty = columns.some((column) => column === '');

  const onRowChange = (rows: string[]) => {
    const someRowsAreEmpty = rows.some((row) => row === '');

    setRows(rows);

    if (!someRowsAreEmpty && rowsError) {
      setRowsError(false);
    }

    if (dataNotValid) return;

    debouncedChange({ ...q, matrix_options: { rows: compact(rows), columns: compact(columns) } });
  };

  const onColumnChange = (columns: string[]) => {
    const someColumnsAreEmpty = columns.some((column) => column === '');

    if (columns.length > 16) {
      setColumnsLimitError(true);
      return;
    } else if (columnsLimitError) {
      setColumnsLimitError(false);
    }

    if (!someColumnsAreEmpty && columnsError) {
      setColumnsError(false);
    }

    setColumns(columns);

    if (dataNotValid) return;

    debouncedChange({ ...q, matrix_options: { rows: compact(rows), columns: compact(columns) } });
  };

  const onRowBlur = (rows: string[]) => {
    setRows(rows);

    if (dataNotValid) return;

    if (someRowsAreEmpty) {
      setRowsError(true);
    }

    onChange({ ...q, matrix_options: { rows: compact(rows), columns: compact(columns) } });
  };

  const onColumnBlur = (columns: string[]) => {
    setColumns(columns);

    if (someColumnsAreEmpty) {
      setColumnsError(true);
    }

    if (dataNotValid) return;

    onChange({ ...q, matrix_options: { rows: compact(rows), columns: compact(columns) } });
  };

  const onAddRow = () => {
    if (someRowsAreEmpty) {
      if (!rowsError) {
        setRowsError(true);
      }

      return;
    }

    onRowChange([...rows, '']);

    focusInput(`field-${q.id}-row-${rows.length}`);
  };

  const onAddColumn = () => {
    if (columns.length === 16) {
      if (!columnsLimitError) {
        setColumnsLimitError(true);
      }

      return;
    }

    if (someColumnsAreEmpty) {
      if (!columnsError) {
        setColumnsError(true);
      }

      return;
    }

    onColumnChange([...columns, '']);

    focusInput(`field-${q.id}-column-${columns.length}`);
  };

  return (
    <div className='flex flex-col gap-6'>
      <fieldset>
        <div className='mb-2 flex items-center justify-between'>
          <Label className='text-xs'>ROWS</Label>
        </div>

        <DraggableFields
          type={`${q.id}-row`}
          shouldValidate={false}
          onChange={onRowChange}
          onBlur={onRowBlur}
          options={rows}
          placeholder='Add label'
        />
        <div className='mt-2 flex items-center'>
          <Button type='button' icon='plus' onClick={onAddRow} medium>
            Add row
          </Button>
        </div>

        {rowsError && (
          <ErrorMessage className='mt-2'>You need to add a label name before adding another row.</ErrorMessage>
        )}
      </fieldset>
      <fieldset>
        <div className='mb-2 flex items-center justify-between'>
          <Label className='text-xs'>COLUMNS</Label>
        </div>

        <DraggableFields
          type={`${q.id}-column`}
          onChange={onColumnChange}
          shouldValidate={false}
          onBlur={onColumnBlur}
          options={columns}
          placeholder='Add label'
        />
        <div className='mt-2 flex items-center'>
          <Button type='button' icon='plus' onClick={onAddColumn} medium>
            Add column
          </Button>
        </div>

        {columnsError && (
          <ErrorMessage className='mt-2'>You need to add a label name before adding another column.</ErrorMessage>
        )}
        {columnsLimitError && <ErrorMessage className='mt-2'>The 16 column limit has been reached.</ErrorMessage>}
      </fieldset>
    </div>
  );
};
